import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {
  StyledSelect,
  SelectSelector,
  SelectInputWrapper,
  SelectSelectionSearch,
  SelectInput,
  SelectSelectionItem,
  SelectIcon,
  Options,
  StyledOption,
  SelectDropDownIconWrapper,
  SelectorWrapper,
  OptionsPortal,
} from './Select.styled'
import {Icon} from '../icon/Icon'

import {theme} from '~/styles/theme'

export type InputProps = {
  children: React.ReactNode
  className?: string
  disabled?: boolean
  size?: 'large' | 'normal' | 'small'
  placeholder?: string
  value?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  noBorder?: boolean
  selectedOption?: ReactNode | ReactNode[]
  showOutsideIcon?: boolean
  noIcon?: boolean
  search?: boolean
  width?: number
  required?: boolean // Only valid if 'noBorder' is false. Sets a red border if the field is required
}

type OptionProps = {
  children: React.ReactNode
  onClick?: () => void
  size?: 'large' | 'normal' | 'small'
}

export const Option: React.FC<OptionProps> = ({
  onClick,
  size = 'normal',
  children,
}) => (
  <StyledOption size={size} onClick={onClick}>
    {children}
  </StyledOption>
)

export const Select: React.FC<InputProps> = ({
  className,
  children,
  disabled,
  size = 'normal',
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  noBorder,
  selectedOption,
  showOutsideIcon,
  noIcon,
  search,
  width,
  required,
}) => {
  const [showOptions, setShowOptions] = useState<boolean>(false)
  const selectRef = useRef<HTMLDivElement | null>(null)
  const optionsRef = useRef<HTMLDivElement | null>(null)
  const hasChildren = useMemo(
    () => React.Children.count(children) > 0,
    [children],
  )
  const [portalPosition, setPortalPosition] = useState<{
    top: number
    left: number
    width: number
    maxHeight: number
  }>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 0,
  })

  useEffect(() => {
    if (!showOptions) return

    const updatePosition = () => {
      if (!selectRef.current) return
      const rect = selectRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top
      const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow
      const availableSpace = openUpward ? spaceAbove : spaceBelow
      const maxHeight = Math.max(120, Math.min(280, availableSpace - 8))
      const top = openUpward ? Math.max(8, rect.top - maxHeight) : rect.bottom

      setPortalPosition({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight,
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [showOptions])

  useEffect(() => {
    if (!showOptions) return

    const handleEscapeClose = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowOptions(false)
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return
      const clickedSelect = selectRef.current?.contains(event.target)
      const clickedOptions = optionsRef.current?.contains(event.target)

      if (!clickedSelect && !clickedOptions) {
        setShowOptions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscapeClose)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeClose)
    }
  }, [showOptions])

  const renderedOptions =
    hasChildren && showOptions ? (
      <OptionsPortal
        top={portalPosition.top}
        left={portalPosition.left}
        width={portalPosition.width}
        maxHeight={portalPosition.maxHeight}
        onClick={() => setShowOptions(false)}
        ref={optionsRef}>
        <Options>{children}</Options>
      </OptionsPortal>
    ) : null

  return (
    <>
      <StyledSelect
        className={className}
        sizeVariant={size}
        disabled={disabled}
        width={width}
        onClick={disabled ? undefined : () => setShowOptions(!showOptions)}
        ref={selectRef}>
        <SelectorWrapper>
          <SelectSelector
            noBorder={noBorder}
            required={required}
            showOptions={hasChildren && showOptions}>
            <SelectInputWrapper sizeVariant={size} disabled={disabled}>
              <SelectSelectionSearch sizeVariant={size} search={search}>
                <SelectInput
                  showInput={!selectedOption}
                  placeholder={placeholder}
                  disabled={disabled}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  readOnly={!search}
                  sizeVariant={size}
                />
              </SelectSelectionSearch>
              <SelectSelectionItem search={search}>
                {selectedOption}
              </SelectSelectionItem>
              {!noIcon && (
                <SelectIcon
                  search={search}
                  showOptions={hasChildren && showOptions && !search}
                  name={search ? 'Search' : 'ArrowDown'}
                  color={theme.colors.grey.grey500}
                />
              )}
            </SelectInputWrapper>
          </SelectSelector>
        </SelectorWrapper>
        {showOutsideIcon && (
          <SelectDropDownIconWrapper>
            <Icon
              flip={showOptions}
              name="ChevronDownLg"
              circled="lg"
              color={theme.colors.grey.grey300}
              size="md"
            />
          </SelectDropDownIconWrapper>
        )}
      </StyledSelect>
      {typeof document !== 'undefined' &&
        renderedOptions &&
        createPortal(renderedOptions, document.body)}
    </>
  )
}
